import React, { useState, useRef } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  FileText, Camera, Eye, Upload, Loader2, CheckCircle2, 
  AlertCircle, User, Download, Printer, Image, X
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { printPoliceForm } from '../../utils/policeFormTemplate';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Pax Document Card Component
const PaxDocumentCard = ({ pax, onViewPhoto, onUploadPhoto, uploading }) => {
  const fileInputRef = useRef(null);
  const hasPassportPhoto = !!pax.passport_photo;
  const hasDocumentFile = !!pax.document_file;
  
  return (
    <div className={`p-3 rounded-lg border ${hasPassportPhoto ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-start gap-3">
        {/* Photo thumbnail */}
        <div 
          className={`w-16 h-20 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
            hasPassportPhoto 
              ? 'border-green-400 bg-white hover:border-green-600' 
              : 'border-dashed border-gray-300 bg-gray-100'
          }`}
          onClick={() => hasPassportPhoto && onViewPhoto(pax)}
        >
          {hasPassportPhoto ? (
            <img 
              src={`${API_URL}/api/uploads/${pax.passport_photo}`}
              alt={`${pax.first_name}`}
              className="w-full h-full object-cover rounded"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : (
            <Camera className="w-6 h-6 text-gray-400" />
          )}
        </div>
        
        {/* Pax info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900">{pax.first_name} {pax.last_name}</p>
            {pax.is_main && (
              <Badge variant="secondary" className="text-xs">Principal</Badge>
            )}
          </div>
          
          <div className="text-xs text-gray-600 mt-1 space-y-0.5">
            {pax.nationality && <p>Nationalité: {pax.nationality}</p>}
            {pax.document_number && <p className="font-mono">Doc: {pax.document_number}</p>}
          </div>
          
          {/* Status indicators */}
          <div className="flex gap-2 mt-2">
            {hasPassportPhoto ? (
              <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Photo OK
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                <AlertCircle className="w-3 h-3 mr-1" />
                Photo manquante
              </Badge>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-1">
          {hasPassportPhoto && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onViewPhoto(pax)}
              className="h-8 w-8 p-0"
              title="Voir la photo"
            >
              <Eye className="w-4 h-4 text-green-600" />
            </Button>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                onUploadPhoto(pax.id, e.target.files[0]);
              }
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading === pax.id}
            className="h-8 w-8 p-0"
            title={hasPassportPhoto ? "Remplacer la photo" : "Ajouter une photo"}
          >
            {uploading === pax.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 text-blue-600" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function DocumentsSection({ reservation, paxList = [], onRefresh }) {
  const [uploading, setUploading] = useState(null);
  const [loadingPolice, setLoadingPolice] = useState(false);
  const [policeFormData, setPoliceFormData] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  
  // Check if police form exists
  const hasPoliceForm = !!reservation.police_form_generated_at;
  const policeFormDate = reservation.police_form_generated_at 
    ? format(new Date(reservation.police_form_generated_at), "dd MMM yyyy 'à' HH:mm", { locale: fr })
    : null;
  
  // Count documents
  const paxWithPhotos = paxList.filter(p => p.passport_photo).length;
  const totalPax = paxList.length;
  const expectedPax = reservation.total_pax || reservation.adults + (reservation.children || 0);
  
  // Upload photo for a PAX
  const handleUploadPhoto = async (paxId, file) => {
    setUploading(paxId);
    try {
      const formData = new FormData();
      formData.append('document', file);
      
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/pax/${paxId}/passport-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Erreur upload');
      }
      
      toast.success('Photo passeport enregistrée');
      onRefresh?.();
    } catch (err) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };
  
  // View/Print Police Form
  const handleViewPoliceForm = async () => {
    setLoadingPolice(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/police-form`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur');
      
      const data = await response.json();
      setPoliceFormData(data);
      
      // Use centralized template
      printPoliceForm(data);
    } catch (err) {
      toast.error('Erreur lors du chargement de la fiche de police');
    } finally {
      setLoadingPolice(false);
    }
  };
  
  // Regenerate Police Form
  const handleRegeneratePoliceForm = async () => {
    setLoadingPolice(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/police-form?regenerate=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur');
      
      const data = await response.json();
      
      // Use centralized template
      printPoliceForm(data);
      
      toast.success('Fiche de police régénérée');
      onRefresh?.();
    } catch (err) {
      toast.error('Erreur lors de la régénération');
    } finally {
      setLoadingPolice(false);
    }
  };
  
  return (
    <div className="space-y-4" data-testid="documents-section">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Documents & Fiche de Police</h3>
        </div>
        <div className="flex items-center gap-2">
          {hasPoliceForm ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Fiche générée
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
              <AlertCircle className="w-3 h-3 mr-1" />
              Pas de fiche
            </Badge>
          )}
        </div>
      </div>
      
      {/* Police Form Section */}
      <div className={`p-4 rounded-xl border-2 ${hasPoliceForm ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${hasPoliceForm ? 'bg-green-100' : 'bg-gray-200'}`}>
              <FileText className={`w-6 h-6 ${hasPoliceForm ? 'text-green-600' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Fiche de Police</p>
              {hasPoliceForm ? (
                <p className="text-xs text-green-700">Générée le {policeFormDate}</p>
              ) : (
                <p className="text-xs text-gray-500">Non générée</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {hasPoliceForm && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleViewPoliceForm}
                  disabled={loadingPolice}
                  className="border-green-300 text-green-700 hover:bg-green-100"
                  data-testid="view-police-form-btn"
                >
                  {loadingPolice ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Printer className="w-4 h-4 mr-1" />
                  )}
                  Imprimer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleRegeneratePoliceForm}
                  disabled={loadingPolice}
                  title="Régénérer avec les dernières données"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </>
            )}
            {!hasPoliceForm && totalPax > 0 && (
              <Button
                size="sm"
                onClick={handleRegeneratePoliceForm}
                disabled={loadingPolice}
                className="bg-primary"
                data-testid="generate-police-form-btn"
              >
                {loadingPolice ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <FileText className="w-4 h-4 mr-1" />
                )}
                Générer
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* PAX Documents Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-600" />
            <p className="text-sm font-medium text-gray-700">
              Photos passeports ({paxWithPhotos}/{totalPax})
            </p>
          </div>
          {totalPax < expectedPax && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              {expectedPax - totalPax} PAX manquant(s)
            </Badge>
          )}
        </div>
        
        {totalPax === 0 ? (
          <div className="text-center py-6 border-2 border-dashed rounded-lg text-gray-500">
            <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucun voyageur enregistré</p>
            <p className="text-xs text-gray-400 mt-1">
              Ajoutez les voyageurs dans l'onglet "Passagers"
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {paxList.map((pax) => (
              <PaxDocumentCard
                key={pax.id}
                pax={pax}
                onViewPhoto={(p) => setViewingPhoto(p)}
                onUploadPhoto={handleUploadPhoto}
                uploading={uploading}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Completeness Alert */}
      {totalPax > 0 && paxWithPhotos < totalPax && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {totalPax - paxWithPhotos} photo(s) passeport manquante(s)
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Ajoutez les photos pour compléter la fiche de police
            </p>
          </div>
        </div>
      )}
      
      {/* Photo Viewer Dialog */}
      <Dialog open={!!viewingPhoto} onOpenChange={() => setViewingPhoto(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              {viewingPhoto?.first_name} {viewingPhoto?.last_name}
            </DialogTitle>
          </DialogHeader>
          {viewingPhoto?.passport_photo && (
            <div className="flex flex-col items-center">
              <img
                src={`${API_URL}/api/uploads/${viewingPhoto.passport_photo}`}
                alt="Passeport"
                className="max-w-full max-h-[60vh] rounded-lg border"
              />
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => window.open(`${API_URL}/api/uploads/${viewingPhoto.passport_photo}`, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewingPhoto(null)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
